import { FIT_TYPE, SPEND_SCALE, fitCurve, parameterCount } from './fitCurve';

export const CURVE_SAMPLES = 60;
export const SPEND_SEARCH_MIN = 500000;
export const SPEND_SEARCH_MAX = 6000000;
export const SPEND_SEARCH_STEP = 5000;
export const SPEND_ABOVE_SEARCH_MAX = 'above-max';

export function minimumTrendPoints(fitType) {
  return parameterCount(fitType) + 1;
}

export function cohortActualCac(cohort) {
  if (!cohort || cohort.isPlanned) {
    return null;
  }
  const spend = Number(cohort.uaSpend);
  const users = Number(cohort.firstMonthUsers);
  if (spend > 0 && users > 0) {
    return spend / users;
  }
  const cac = Number(cohort.cac);
  return cac > 0 ? cac : null;
}

export function trendTrainingPoints(cohorts, selectedIds) {
  const selected = new Set(selectedIds || []);
  return (cohorts || [])
    .filter((cohort) => selected.has(cohort.id))
    .map((cohort) => {
      const cac = cohortActualCac(cohort);
      if (!(cohort.uaSpend > 0) || !(cac > 0)) {
        return null;
      }
      return {
        id: cohort.id,
        cohortMonth: cohort.cohortMonth,
        label: cohort.label,
        uaSpend: cohort.uaSpend,
        x: cohort.uaSpend / SPEND_SCALE,
        y: cac,
      };
    })
    .filter(Boolean);
}

function logFitted(fit, scaledSpend) {
  return fit.coefficients.a + fit.coefficients.b * Math.log(scaledSpend);
}

function residual(fit, point) {
  if (fit.fitType === FIT_TYPE.POWER) {
    return Math.log(point.y) - logFitted(fit, point.x);
  }
  const fitted = fit.evaluate(point.x);
  if (!Number.isFinite(fitted)) {
    return null;
  }
  return point.y - fitted;
}

function residualStdError(fit, points) {
  const residuals = points
    .map((point) => residual(fit, point))
    .filter((value) => Number.isFinite(value));
  const degreesOfFreedom = residuals.length - parameterCount(fit.fitType);
  if (degreesOfFreedom < 1) {
    return null;
  }
  const sumOfSquares = residuals.reduce((sum, value) => sum + value * value, 0);
  return Math.sqrt(sumOfSquares / degreesOfFreedom);
}

function rSquaredOnCac(fit, points) {
  const fitted = points.map((point) => fit.evaluate(point.x));
  if (fitted.some((value) => !Number.isFinite(value))) {
    return null;
  }
  const mean = points.reduce((sum, point) => sum + point.y, 0) / points.length;
  const total = points.reduce((sum, point) => sum + (point.y - mean) ** 2, 0);
  if (total === 0) {
    return 1;
  }
  const residualSum = points.reduce(
    (sum, point, index) => sum + (point.y - fitted[index]) ** 2,
    0
  );
  return 1 - residualSum / total;
}

export function sampleSpends(points) {
  const spends = (points || []).map((point) => point.uaSpend).filter((spend) => spend > 0);
  if (!spends.length) {
    return [];
  }
  const lowest = Math.min(...spends);
  const highest = Math.max(...spends);
  if (highest === lowest) {
    return Array(CURVE_SAMPLES).fill(lowest);
  }
  const step = (highest - lowest) / (CURVE_SAMPLES - 1);
  return Array.from({ length: CURVE_SAMPLES }, (_, index) => lowest + step * index);
}

function bandValue(fit, scaledSpend, sigma, multiplier) {
  if (fit.fitType === FIT_TYPE.POWER) {
    return Math.exp(logFitted(fit, scaledSpend) + multiplier * sigma);
  }
  const fitted = fit.evaluate(scaledSpend);
  if (!Number.isFinite(fitted) || !Number.isFinite(sigma)) {
    return null;
  }
  return fitted + multiplier * sigma;
}

export function evaluateTrendCurve(fit, sigma, spends) {
  return (spends || [])
    .map((spend) => {
      const scaledSpend = spend / SPEND_SCALE;
      const fitted = fit.evaluate(scaledSpend);
      if (!Number.isFinite(fitted)) {
        return null;
      }
      const bandSigma = sigma > 0 ? sigma : 0;
      return {
        spend,
        fitted,
        low1: bandValue(fit, scaledSpend, bandSigma, -1),
        high1: bandValue(fit, scaledSpend, bandSigma, 1),
        low2: bandValue(fit, scaledSpend, bandSigma, -2),
        high2: bandValue(fit, scaledSpend, bandSigma, 2),
      };
    })
    .filter(Boolean);
}

export function spendWhereFitReaches(fit, targetCac) {
  if (!fit || !(targetCac > 0)) {
    return null;
  }
  for (
    let spend = SPEND_SEARCH_MIN;
    spend <= SPEND_SEARCH_MAX + SPEND_SEARCH_STEP / 2;
    spend += SPEND_SEARCH_STEP
  ) {
    const fitted = fit.evaluate(spend / SPEND_SCALE);
    if (Number.isFinite(fitted) && fitted >= targetCac) {
      return spend;
    }
  }
  return SPEND_ABOVE_SEARCH_MAX;
}

function latestActualVersusFit(cohorts, selectedIds, fit, sigma) {
  if (!fit || !(sigma > 0)) {
    return null;
  }
  const selected = new Set(selectedIds || []);
  const latest = [...(cohorts || [])]
    .reverse()
    .find((cohort) => selected.has(cohort.id) && cohortActualCac(cohort) > 0);
  if (!latest || !(latest.uaSpend > 0)) {
    return null;
  }
  const point = {
    x: latest.uaSpend / SPEND_SCALE,
    y: cohortActualCac(latest),
  };
  const value = residual(fit, point);
  if (!Number.isFinite(value)) {
    return null;
  }
  return { label: latest.label, z: value / sigma };
}

export function buildCacTrend({ cohorts, selectedIds, fitType, maxCac }) {
  const points = trendTrainingPoints(cohorts, selectedIds);
  const minimumPoints = minimumTrendPoints(fitType);
  const empty = {
    points,
    minimumPoints,
    fit: null,
    sigma: null,
    r2: null,
    samples: [],
    latest: null,
    spendAtMaxCac: null,
  };
  if (points.length < minimumPoints) {
    return empty;
  }

  const fit = fitCurve(fitType, points);
  if (!fit) {
    return empty;
  }

  const sigma = residualStdError(fit, points);
  return {
    points,
    minimumPoints,
    fit,
    sigma,
    r2: rSquaredOnCac(fit, points),
    samples: evaluateTrendCurve(fit, sigma, sampleSpends(points)),
    latest: latestActualVersusFit(cohorts, selectedIds, fit, sigma),
    spendAtMaxCac: maxCac > 0 ? spendWhereFitReaches(fit, maxCac) : null,
  };
}
